import { useState, type ReactNode } from 'react';
import { HOME3_DESTINATION_LABEL_KEY, type Home3Destination } from '../../../lib/home3-feed';
import { createGoalContext, newGoalSeedKey } from '../../../lib/goals';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { useWorkspaces } from '../../../lib/workspaces';
import { AppsBody } from '../apps/AppsBody';
import { OutreachBody } from '../contacts/OutreachBody';
import { AgentWorkspaceFilesBody } from '../files/AgentWorkspaceFilesBody';
import { GoalsBody } from '../goals/GoalsBody';
import { WorkspaceOpportunities } from '../goals/WorkspaceOpportunities';
import { RoutinesBody } from '../routines/RoutinesBody';
import { TasksBody } from '../tasks/TasksBody';
import { Button } from '../ui';
import { CategoryHeader, CategoryPage } from '../category-view/CategoryView';
import { HOME3_PAGE } from './Home3Feed';
import styles from './Home3CategoryView.module.css';

/** The create button's label per category. Every one but Tasks and Apps is the page's own existing CTA.
 *  Files has none: the explorer's own "Add ⌄" menu is its create. */
const CREATE_LABEL_KEY: Record<Exclude<Home3Destination, 'files'>, string> = {
  tasks: 'categoryView.tasks.createCta',
  goals: 'goals.create.cta',
  routines: 'categoryView.routines.createCta',
  apps: 'apps.create.cta',
  outreach: 'contacts.addContact',
};

/** The first message of the chat a create button opens. Goals is handled on its own. */
const CREATE_SEED_KEY: Record<Exclude<Home3Destination, 'goals' | 'files'>, string> = {
  tasks: 'tasks.create.prompt',
  routines: 'routines.create.prompt',
  apps: 'apps.create.prompt',
  outreach: 'contacts.addContactSeed',
};

/**
 * One category's own page, under the home's tiles — the same body the standalone screen renders, across
 * every Space (Space picker at "All Spaces"). The body draws the shared category layout (Figma
 * 21055:246799 — `CategoryView`): its title and description, its filters, and a toolbar whose end holds
 * the create button this view hands it.
 *
 * The bodies run in their `onHome` mode: the home's URL is the home's, so a body keeps its Space pick
 * and tabs in its own state, and drops its own create button so this one is the only one. The host
 * mounts this keyed by category, so opening a category always starts at "All Spaces".
 */
export function Home3CategoryView({ destination, regionId }: { destination: Home3Destination; regionId: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const workspacesState = useWorkspaces();
  // Only Goals scopes its seed to a Space, so only Goals reports its pick.
  const [goalSpaceId, setGoalSpaceId] = useState('');
  const title = t(HOME3_DESTINATION_LABEL_KEY[destination]);

  const create = (category: Exclude<Home3Destination, 'files'>) => {
    logPageClickBi({ page_name: HOME3_PAGE, button_name: `create_${category}` });
    if (category === 'goals') {
      const roster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
      const name = (roster.find((w) => w.id === goalSpaceId)?.name ?? '').trim();
      openSideChat({
        kind: 'new',
        message: t(newGoalSeedKey(goalSpaceId ? name : undefined), { workspace: name }),
        context: createGoalContext(goalSpaceId || undefined, name),
      });
      return;
    }
    openSideChat({ kind: 'new', message: t(CREATE_SEED_KEY[category]) });
  };

  const action =
    destination === 'files' ? null : (
      <Button onClick={() => create(destination)}>
        {t(CREATE_LABEL_KEY[destination])}
      </Button>
    );

  return (
    <section id={regionId} className={styles.view} aria-label={title}>
      <CategoryBody destination={destination} action={action} onGoalSpaceChange={setGoalSpaceId} />
    </section>
  );
}

function CategoryBody({
  destination,
  action,
  onGoalSpaceChange,
}: {
  destination: Home3Destination;
  action: ReactNode;
  onGoalSpaceChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  switch (destination) {
    case 'tasks':
      return <TasksBody onHome action={action} />;
    case 'goals':
      // The suggestions close the page, under the goals themselves (Figma 21055:254336).
      return (
        <>
          <GoalsBody onHome page={HOME3_PAGE} onSpaceChange={onGoalSpaceChange} action={action} />
          <WorkspaceOpportunities page={HOME3_PAGE} />
        </>
      );
    case 'routines':
      return <RoutinesBody onHome action={action} />;
    case 'files':
      // No button of ours: the explorer's own "Add ⌄" (upload, or a new file) is the band's create, as
      // in the design, and it already knows which folder a file lands in.
      return <AgentWorkspaceFilesBody onHome />;
    case 'apps':
      // Apps keeps its own body; it only takes the shared header, with the create button at its end.
      return (
        <CategoryPage>
          <CategoryHeader title={t('categoryView.apps.title')} description={t('categoryView.apps.description')} filters={action} />
          <AppsBody />
        </CategoryPage>
      );
    case 'outreach':
      return <OutreachBody onHome action={action} />;
  }
}
